import { ChangeEvent, FocusEvent, useEffect, useRef, useState } from 'react';
import { map } from 'rxjs/operators';

import { Checkbox } from '../checkbox/Checkbox';
import { CheckboxGroup } from '../checkbox/CheckboxGroup';
import { FormField } from '../form-field/FormField';
import { FormControl } from '../models/FormControl';
import { ValidationErrorList } from '../validation';

import './PasswordFormField.dark.scss';
import './PasswordFormField.light.scss';

interface Props {
  /**
   * Label of this form field
   */
  label: string;

  /**
   * The model used to keep track of this form field's value
   */
  formControl: FormControl<string>;
  showPasswordHint?: boolean;
}

/**
 * This component renders an input field that spans only a single line,
 * it's used to collect input for short values, hence its name
 *
 * @param props
 * @returns
 */
export function PasswordFormField({ label, formControl, showPasswordHint }: Props) {
  const [inputValue, setInputValue] = useState(formControl.getValue());
  const [shouldShowPassword, setShouldShowPassword] = useState(false);
  const showPasswordFormControlRef = useRef(new FormControl([]));
  const onInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputField = event.target;
    const newValue = inputField.value.trim();

    setInputValue(newValue);
    formControl.setValue(newValue);
  };
  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    if (formControl.isPristine()) {
      formControl.markDirty();
      formControl.setValue(event.target.value.trim());
    }
  };

  useEffect(() => {
    const subscription = showPasswordFormControlRef.current.valueChanges()
      .pipe(map(value => value.length === 1))
      .subscribe({
        next: setShouldShowPassword
      });
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    setInputValue(formControl.getValue());
  }, [formControl]);

  return (
    <FormField
      label={label}
      className='password-form-field'
      formControl={formControl}>
      <input
        className='password-form-field__input-field'
        type={shouldShowPassword ? 'text' : 'password'}
        value={inputValue}
        onBlur={onBlur}
        onChange={onInputValueChange} />
      {
        showPasswordHint
          ? (
            <span className='password-form-field__password-hint'>
              Password must be 8 characters or longer and cannot contain spaces
            </span>
          ) : (
            <ValidationErrorList
              formLabel={label}
              formControl={formControl} />
          )
      }
      <CheckboxGroup formControl={showPasswordFormControlRef.current}>
        <Checkbox
          label='Show password'
          value={false} />
      </CheckboxGroup>
    </FormField>
  );

}
